import { memo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  decay: number;
}

const COLORS = [
  "#ff577f",
  "#ff884b",
  "#ffd384",
  "#fff9b0",
  "#3ec1d3",
  "#6c63ff",
  "#a855f7",
  "#22c55e",
  "#f43f5e",
  "#0ea5e9",
];

// Widget geometry — kept in sync with setup-guide-widget.module.css and
// setup-guide-host.module.css. Used to anchor the particle emission
// origin to the widget's center so particles appear to launch from
// behind the panel.
const WIDGET_WIDTH = 380;
const WIDGET_RIGHT_INSET = 28;
const WIDGET_BOTTOM_INSET = 28;
const ASSUMED_WIDGET_HEIGHT = 440;

function createParticle(originX: number, originY: number): Particle {
  // Strong upward fountain spray with a wide horizontal spread so
  // particles emerge along all visible edges of the widget rather than
  // streaming up in a narrow column.
  const horizontalSpread = (Math.random() - 0.5) * 12;
  const upwardSpeed = 9 + Math.random() * 6;
  return {
    x: originX + (Math.random() - 0.5) * 60,
    y: originY,
    vx: horizontalSpread,
    vy: -upwardSpeed,
    width: 6 + Math.random() * 5,
    height: 4 + Math.random() * 4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.2,
    opacity: 1,
    decay: 0.005 + Math.random() * 0.004,
  };
}

interface WidgetConfettiProps {
  particleCount?: number;
  duration?: number;
}

/**
 * Confetti rendered to a full-viewport canvas via portal so the
 * particles can fly past the widget's edges. Emission origin is
 * anchored to the widget's center; the widget sits at z-index 1000
 * and this canvas at 999, so particles emerge from "behind" the
 * widget as they leave its bounds.
 *
 * FIXME: this is the second copy of the same confetti physics (the
 * other lives in redo/onboarding/app/src/components/ui/confetti.tsx).
 * Extract a parameterized `<Confetti />` into @redotech/react-animation
 * and replace both call sites.
 */
export const WidgetConfetti = memo(function WidgetConfetti({
  particleCount = 110,
  duration = 2800,
}: WidgetConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Widget center in viewport coordinates.
    const originX = width - WIDGET_RIGHT_INSET - WIDGET_WIDTH / 2;
    const originY = height - WIDGET_BOTTOM_INSET - ASSUMED_WIDGET_HEIGHT / 2;

    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(createParticle(originX, originY));
    }

    let animationId: number;
    let startTime: number | undefined;
    const maxDuration = duration + 1500;

    function animate(timestamp: number) {
      if (startTime === undefined) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;
      if (elapsed > maxDuration) {
        ctx!.clearRect(0, 0, width, height);
        return;
      }
      ctx!.clearRect(0, 0, width, height);
      let alive = 0;
      for (const particle of particles) {
        const p = particle!;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.18;
        p.vx *= 0.992;
        p.rotation += p.rotationSpeed;
        p.opacity -= p.decay;
        if (p.opacity <= 0) {
          continue;
        }
        const cos = Math.cos(p.rotation);
        const sin = Math.sin(p.rotation);
        ctx!.globalAlpha = p.opacity;
        ctx!.fillStyle = p.color;
        ctx!.setTransform(
          cos * dpr,
          sin * dpr,
          -sin * dpr,
          cos * dpr,
          p.x * dpr,
          p.y * dpr,
        );
        ctx!.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
        alive++;
      }
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx!.globalAlpha = 1;
      if (alive > 0) {
        animationId = requestAnimationFrame(animate);
      }
    }
    animationId = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [particleCount, duration]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        // Below the widget (z-index 1000 on the dock) so particles
        // appear to emerge from behind it.
        zIndex: 999,
      }}
    />,
    document.body,
  );
});
